/**
 * Ethiopian (Amete Mihret) <-> Gregorian calendar conversion, via the Julian Day Number.
 * Dependency-free and offline. Ported from ADHERE-plus `public/model/ethiopian.js` (v10.8.1).
 *
 * Rules that this module enforces (each one a real ADHERE bug or near-miss):
 * - Dates are plain values ('YYYY-MM-DD' or {year, month, day}), never `Date` objects, so the
 *   device's time zone can never shift a calendar date.
 * - PAGUME IS NOT A 30-DAY MONTH. The thirteenth month has 5 days, 6 in a leap year
 *   (Ethiopian year mod 4 == 3). ADHERE's picker once accepted "Pagume 20", which resolved
 *   silently to a date three weeks into the next year. A date that does not exist is refused.
 */
export const ETH_MONTHS_EN = [
    'Meskerem',
    'Tikimt',
    'Hidar',
    'Tahsas',
    'Tir',
    'Yekatit',
    'Megabit',
    'Miyazia',
    'Ginbot',
    'Sene',
    'Hamle',
    'Nehase',
    'Pagume',
];
export const ETH_MONTHS_AM = [
    'መስከረም',
    'ጥቅምት',
    'ህዳር',
    'ታህሣስ',
    'ጥር',
    'የካቲት',
    'መጋቢት',
    'ሚያዝያ',
    'ግንቦት',
    'ሰኔ',
    'ሐምሌ',
    'ነሐሴ',
    'ጳጉሜን',
];
/** Supported Gregorian range. Outside it, conversion is refused rather than guessed. */
export const MIN_GREGORIAN_YEAR = 1900;
export const MAX_GREGORIAN_YEAR = 2200;
const ETH_EPOCH_OFFSET = 1723856; // JDN offset used by the Amete Mihret era arithmetic
const ETH_JDN_BASE = 1724220;
function gregorianToJdn(y, m, d) {
    const a = Math.floor((14 - m) / 12);
    const y2 = y + 4800 - a;
    const m2 = m + 12 * a - 3;
    return (d +
        Math.floor((153 * m2 + 2) / 5) +
        365 * y2 +
        Math.floor(y2 / 4) -
        Math.floor(y2 / 100) +
        Math.floor(y2 / 400) -
        32045);
}
function jdnToGregorian(jdn) {
    const a = jdn + 32044;
    const b = Math.floor((4 * a + 3) / 146097);
    const c = a - Math.floor((146097 * b) / 4);
    const d = Math.floor((4 * c + 3) / 1461);
    const e = c - Math.floor((1461 * d) / 4);
    const m = Math.floor((5 * e + 2) / 153);
    return {
        d: e - Math.floor((153 * m + 2) / 5) + 1,
        m: m + 3 - 12 * Math.floor(m / 10),
        y: 100 * b + d - 4800 + Math.floor(m / 10),
    };
}
function ethiopianToJdn(e) {
    return ETH_JDN_BASE + 365 * (e.year - 1) + Math.floor(e.year / 4) + 30 * (e.month - 1) + e.day;
}
const pad2 = (n) => String(n).padStart(2, '0');
/** True when the Ethiopian year has a 6-day Pagume. */
export function isEthLeapYear(ethYear) {
    return Number.isInteger(ethYear) && ((ethYear % 4) + 4) % 4 === 3;
}
/** Days in an Ethiopian month: 30, or 5/6 for Pagume. 0 for a month that does not exist. */
export function ethDaysInMonth(ethYear, ethMonth) {
    if (!Number.isInteger(ethMonth) || ethMonth < 1 || ethMonth > 13)
        return 0;
    if (ethMonth <= 12)
        return 30;
    return isEthLeapYear(ethYear) ? 6 : 5;
}
/** True only for an Ethiopian date that exists and lies inside the supported range. */
export function isValidEthDate(e) {
    if (![e.year, e.month, e.day].every(Number.isInteger))
        return false;
    if (e.day < 1 || e.day > ethDaysInMonth(e.year, e.month))
        return false;
    const g = jdnToGregorian(ethiopianToJdn(e));
    return g.y >= MIN_GREGORIAN_YEAR && g.y <= MAX_GREGORIAN_YEAR;
}
/** Parse a strict 'YYYY-MM-DD' Gregorian date. Returns null if it is malformed or does not exist. */
export function parseIsoDate(iso) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (!match)
        return null;
    const y = Number(match[1]);
    const m = Number(match[2]);
    const d = Number(match[3]);
    if (y < MIN_GREGORIAN_YEAR || y > MAX_GREGORIAN_YEAR)
        return null;
    const back = jdnToGregorian(gregorianToJdn(y, m, d));
    if (back.y !== y || back.m !== m || back.d !== d)
        return null; // e.g. 2026-02-30
    return { y, m, d };
}
/** Gregorian 'YYYY-MM-DD' -> Ethiopian date. Returns null for an invalid or out-of-range date. */
export function isoToEth(iso) {
    const g = parseIsoDate(iso);
    if (!g)
        return null;
    const jdn = gregorianToJdn(g.y, g.m, g.d);
    const r = (((jdn - ETH_EPOCH_OFFSET) % 1461) + 1461) % 1461;
    const n = (r % 365) + 365 * Math.floor(r / 1460);
    const year = 4 * Math.floor((jdn - ETH_EPOCH_OFFSET) / 1461) + Math.floor(r / 365) - Math.floor(r / 1460);
    return { year, month: Math.floor(n / 30) + 1, day: (n % 30) + 1 };
}
/** Ethiopian date -> Gregorian 'YYYY-MM-DD'. Returns null for a date that does not exist. */
export function ethToIso(e) {
    if (!isValidEthDate(e))
        return null;
    const g = jdnToGregorian(ethiopianToJdn(e));
    return `${g.y}-${pad2(g.m)}-${pad2(g.d)}`;
}
/** "Tikimt 1, 2019 E.C." or "ጥቅምት 1, 2019 ዓ.ም". */
export function formatEth(e, lang = 'en') {
    const names = lang === 'am' ? ETH_MONTHS_AM : ETH_MONTHS_EN;
    const name = names[e.month - 1];
    if (!name || !isValidEthDate(e))
        throw new RangeError(`Not a valid Ethiopian date: ${JSON.stringify(e)}`);
    return lang === 'am' ? `${name} ${e.day}, ${e.year} ዓ.ም` : `${name} ${e.day}, ${e.year} E.C.`;
}
//# sourceMappingURL=ethiopian.js.map